import { Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { SensitiveInlineText } from "@/components/common/Sensitive";
import { useUserOptional } from "@/auth/UserContext";
import { firstName, resolveDisplayName } from "@/lib/userDisplay";
import type { KaiWelcomePrompt } from "./kaiWelcomePrompts";

interface KaiWelcomeProps {
  prompts: KaiWelcomePrompt[];
  onSelectPrompt: (text: string) => void;
  /** Personalized prompts are loading; dim the (fallback) chips meanwhile. */
  loading?: boolean;
  className?: string;
}

/** Empty-state hero with starter prompts. */
export function KaiWelcome({
  prompts,
  onSelectPrompt,
  loading = false,
  className,
}: KaiWelcomeProps) {
  const userCtx = useUserOptional();
  const name = firstName(
    resolveDisplayName(
      userCtx?.profile?.name ?? userCtx?.user?.name,
      userCtx?.profile?.email ?? userCtx?.user?.email,
    ),
  );

  return (
    <div className={cn("w-full max-w-2xl text-center", className)}>
      <div className="relative mx-auto mb-5 size-[4.25rem]">
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-8 rounded-full bg-[radial-gradient(circle,hsl(var(--neon-blue)/0.42)_0%,hsl(var(--neon-blue)/0.16)_42%,transparent_72%)] blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-4 rounded-full bg-[radial-gradient(circle,hsl(var(--neon-blue)/0.28)_0%,transparent_68%)] blur-lg"
        />
        <div className="glass-panel glass-no-hover relative flex size-full items-center justify-center rounded-2xl text-primary shadow-[0_0_64px_-2px_hsl(var(--neon-blue)/0.58),0_0_120px_-24px_hsl(var(--neon-blue)/0.38)] ring-1 ring-primary/14">
          <Sparkles className="size-8 drop-shadow-[0_0_10px_hsl(var(--neon-blue)/0.55)]" strokeWidth={1.75} />
        </div>
      </div>
      <h1 className="text-xl font-semibold tracking-tight sm:text-2xl md:text-3xl">
        Hi {name}, what can I help with?
      </h1>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground md:mt-2 md:text-base">
        Ask about bookings, rates, rebook opportunities, allocations, or spot
        lanes — freight ops in plain language.
      </p>
      <div
        className={cn(
          "mt-4 grid w-full gap-1.5 transition-opacity md:mt-8 md:gap-2 sm:grid-cols-2",
          loading && "opacity-60",
        )}
      >
        {prompts.map((prompt) => (
          <button
            key={prompt.id}
            type="button"
            onClick={() => onSelectPrompt(prompt.text)}
            className="glass-panel rounded-xl px-3 py-2.5 text-left text-sm md:px-4 md:py-3"
          >
            <SensitiveInlineText text={prompt.text} />
          </button>
        ))}
      </div>
    </div>
  );
}
